"use client";

import { NativeLink as Link } from "@/components/native-link";
import { useEffect, useMemo, useState } from "react";
import { Activity, Bot, Clock3, Copy, Download, FileText, Filter, Flag, MoreHorizontal, Plus, RadioTower, Search, Sparkles, Trash2, Video } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { MetricCard, PageHeader, Pill } from "@/components/pulse-shell";
import { fusionTooltip, VoiceSparkline } from "@/components/perception-channels";
import { api, type Agent, type SessionSummary } from "@/lib/api";
import { downloadSessionReportPdf } from "@/lib/session-report-pdf";
import { useAuthProfile, useRoleImpersonation } from "@/lib/auth-context";
import { agentIdsEquivalent } from "@/lib/agent-ids";
import {
  filterAgentsByAllowlist,
  filterSessionsForRolePreview,
  peerRestrictRoleIdsFromRoles,
} from "@/lib/session-role-scope";
import { hasPermission } from "@/lib/users-rbac";
import { useNavigate } from "react-router-dom";
import {
  displayTopic,
  displayUserName,
  formatDuration,
  formatStarted,
  initialsFrom,
  avatarGradientForUserKey,
  sessionUserAvatarKey,
  moodLabel,
  moodTone,
  statusTone,
  toDisplayStatus,
  type DisplayStatus,
} from "@/lib/sessions";
import { sttUnrecognizedDisplayText } from "@/lib/stt-labels";

const FILTERS: DisplayStatus[] = ["All", "Live", "Completed", "Review", "Failed", "Abandoned"];
const PULSE_ORIGIN = "https://pulse.igentify.in";
const PAGE_SIZE = 50;

const AGENT_META: Record<string, { name: string; role: string; color: string }> = {
  nova: { name: "Nova", role: "Customer Success", color: "#176fff" },
  avery: { name: "Avery", role: "Sales Guide", color: "#8057e8" },
  orion: { name: "Orion", role: "Onboarding", color: "#0aa878" },
  mika: { name: "Mika", role: "Medical Assistant", color: "#ec8a34" },
  emma: { name: "Emma", role: "Doctor", color: "#0d9488" },
};

function agentMeta(agentId?: string | null) {
  const key = String(agentId || "").toLowerCase();
  return AGENT_META[key] || {
    name: agentId || "Agent",
    role: "Conversation",
    color: "#176fff",
  };
}

export function SessionsPage() {
  const profile = useAuthProfile();
  const { impersonation } = useRoleImpersonation();
  const showBeta = hasPermission(profile?.permissions, "ui.beta_features");
  const canExportReport = hasPermission(profile?.permissions, "sessions.export_report");
  const canViewFinance = hasPermission(profile?.permissions, "finance.view");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<DisplayStatus>("All");
  const [agentFilter, setAgentFilter] = useState("All agents");
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [agentsCatalog, setAgentsCatalog] = useState<Agent[]>([]);
  const [pickedAgent, setPickedAgent] = useState("nova");
  const [menuId, setMenuId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [peerRestrictRoleIds, setPeerRestrictRoleIds] = useState<Set<string> | null>(null);
  const navigate = useNavigate();
  const showNewSession = agentsCatalog.length > 0;

  useEffect(() => {
    if (!impersonation) {
      setPeerRestrictRoleIds(null);
      return;
    }
    void api.roles
      .list({ limit: 200, offset: 0 })
      .then((out) => {
        setPeerRestrictRoleIds(peerRestrictRoleIdsFromRoles(out.roles || []));
      })
      .catch(() => {
        setPeerRestrictRoleIds(new Set());
      });
  }, [impersonation]);

  function applySessionScope(list: SessionSummary[]): SessionSummary[] {
    if (!impersonation) return list;
    return filterSessionsForRolePreview(list, profile, {
      peerRestrictRoleIds: peerRestrictRoleIds ?? undefined,
    });
  }

  function applyAgentScope(list: Agent[]): Agent[] {
    return filterAgentsByAllowlist(list, profile?.allowedAgentIds);
  }

  useEffect(() => {
    if (!menuId) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.(".row-action-wrap")) return;
      setMenuId(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuId]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const res = await api.listSessions({ limit: PAGE_SIZE, offset: 0 });
        const scoped = applySessionScope(res.sessions || []);
        setSessions(scoped);
        setTotalSessions(
          impersonation
            ? scoped.length
            : Number(res.total || res.sessions?.length || 0),
        );
        setHasMore(Boolean(res.hasMore));
        setNextOffset(res.nextOffset ?? null);
      } catch (e) {
        setSessions([]);
        setTotalSessions(0);
        setHasMore(false);
        setNextOffset(null);
        setToast(e instanceof Error ? e.message : "Failed to load sessions");
      } finally {
        setLoading(false);
      }
      try {
        const list = await api.listAgents();
        const published = (list.agents || []).filter((a) => a.published !== false);
        const catalog = applyAgentScope(published.length ? published : list.agents || []);
        setAgentsCatalog(catalog);
        if (catalog[0]?.agentId) setPickedAgent(catalog[0].agentId);
      } catch {
        const allowedIds = profile?.allowedAgentIds || [];
        const fallback =
          allowedIds.length > 0
            ? allowedIds.map((agentId) => ({ agentId, name: agentId }))
            : [
                { agentId: "nova", name: "Nova" },
                { agentId: "avery", name: "Avery" },
                { agentId: "orion", name: "Orion" },
                { agentId: "mika", name: "Mika" },
              ];
        const catalog = applyAgentScope(fallback);
        setAgentsCatalog(catalog);
        if (catalog[0]?.agentId) setPickedAgent(catalog[0].agentId);
      }
    })();
    // Re-filter when entering/leaving role preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply* closes over impersonation/profile
  }, [impersonation?.roleId, profile?.roleId, profile?.allowedAgentIds?.join("|")]);

  async function loadMore() {
    if (!hasMore || nextOffset == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.listSessions({ limit: PAGE_SIZE, offset: nextOffset });
      const incoming = applySessionScope(res.sessions || []);
      setSessions((prev) => {
        const seen = new Set(prev.map((s) => s.sessionId));
        const next = [...prev, ...incoming.filter((s) => !seen.has(s.sessionId))];
        if (impersonation) setTotalSessions(next.length);
        return next;
      });
      if (!impersonation) setTotalSessions(Number(res.total || 0));
      setHasMore(Boolean(res.hasMore));
      setNextOffset(res.nextOffset ?? null);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  }
  const sessionAgentIds = useMemo(() => {
    const ids = Array.from(new Set(sessions.map((s) => s.agentId).filter(Boolean))) as string[];
    return ids.sort();
  }, [sessions]);

  const shareLink = `${PULSE_ORIGIN}/?agent=${encodeURIComponent(pickedAgent)}`;

  const rows = useMemo(
    () =>
      sessions.filter((s) => {
        const display = s.displayStatus || toDisplayStatus(s.status);
        if (status !== "All" && display !== status) return false;
        if (agentFilter !== "All agents" && !agentIdsEquivalent(agentFilter, s.agentId || "")) return false;
        const hay = `${s.sessionId} ${s.userName || ""} ${s.userEmail || ""} ${s.lastUserText || ""} ${s.agentId || ""}`.toLowerCase();
        return hay.includes(query.toLowerCase());
      }),
    [sessions, status, agentFilter, query],
  );

  const liveCount = sessions.filter((s) => (s.displayStatus || toDisplayStatus(s.status)) === "Live").length;
  const todayCount = sessions.filter((s) => {
    if (!s.createdAt) return false;
    return new Date(s.createdAt).toDateString() === new Date().toDateString();
  }).length;
  const avgDuration = (() => {
    const vals = sessions.map((s) => Number(s.durationSec || 0)).filter((n) => n > 0);
    if (!vals.length) return "—";
    const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    return formatDuration(avg, "ended");
  })();
  const positivePct = (() => {
    const scored = sessions.filter((s) => s.qualityScore != null);
    if (!scored.length) return "—";
    const ok = scored.filter((s) => Number(s.qualityScore) >= 70).length;
    return `${((ok / scored.length) * 100).toFixed(1)}%`;
  })();

  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2800);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareLink);
      showToast("Link copied — share with a signed-in user");
    } catch {
      showToast(shareLink);
    }
  }

  async function copySessionId(id: string) {
    const short = id.replace(/^lk-/, "").slice(0, 8);
    try {
      await navigator.clipboard.writeText(id);
      showToast(`Copied ${short}`);
    } catch {
      showToast(id);
    }
    setMenuId(null);
  }

  async function flagSession(id: string) {
    setBusyId(id);
    try {
      await api.flagSession(id);
      setSessions((prev) => prev.map((s) => (s.sessionId === id ? { ...s, status: "review", displayStatus: "Review" } : s)));
      showToast("Flagged for review");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Flag failed");
    } finally {
      setBusyId(null);
      setMenuId(null);
    }
  }

  async function deleteSessionConfirmed() {
    if (!deleteId) return;
    const id = deleteId;
    setBusyId(id);
    try {
      await api.deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.sessionId !== id));
      showToast("Session deleted");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setBusyId(null);
      setDeleteId(null);
      setMenuId(null);
    }
  }

  async function downloadTranscript(id: string) {
    setBusyId(id);
    try {
      const detail = await api.getSession(id);
      const name = displayUserName(detail);
      const lines = (detail.transcripts?.length
        ? detail.transcripts
        : (detail.turns || []).map((t) => ({ role: t.role, text: t.text, startMs: t.startMs }))) || [];
      const body = [
        `Igentify Pulse session ${id}`,
        `Participant: ${name}`,
        "",
        ...lines.map((l) => {
          const text = sttUnrecognizedDisplayText(
            l.text,
            (l as { unrecognized?: boolean }).unrecognized,
          );
          return `${l.role === "user" ? name : "Agent"}: ${text}`;
        }),
      ].join("\n");
      const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pulse-transcript-${id.replace(/^lk-/, "").slice(0, 8)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("Transcript downloaded");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Download failed");
    } finally {
      setBusyId(null);
      setMenuId(null);
    }
  }

  async function exportReport(id: string) {
    setBusyId(id);
    try {
      const detail = await api.getSession(id);
      downloadSessionReportPdf(detail, { includeCost: canViewFinance });
      showToast("Report exported");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusyId(null);
      setMenuId(null);
    }
  }

  return <>
    <PageHeader
      eyebrow="Session operations"
      title="Sessions"
      subtitle="Monitor live conversations, review completed interactions, and inspect perception signals."
      actions={
        showBeta || showNewSession ? (
          <>
            {showBeta ? <button className="btn"><Download />Export CSV</button> : null}
            {showNewSession ? (
              <button className="btn primary" onClick={() => setOpen(true)}><Plus />New session</button>
            ) : null}
          </>
        ) : undefined
      }
    />
    {showBeta ? (
      <div className="metrics">
        <MetricCard label="Sessions today" value={String(todayCount)} icon={<RadioTower />} foot={<><span className="delta">{sessions.length} listed</span><span>total</span></>} />
        <MetricCard label="Live now" value={String(liveCount)} icon={<Activity />} tone="green" foot={<><span className="dot" style={{ color: "#0aa878" }} /><span>active statuses</span></>} />
        <MetricCard label="Avg. duration" value={avgDuration} icon={<Clock3 />} tone="purple" foot={<><span>from recordings</span></>} />
        <MetricCard label="Positive outcome" value={positivePct} icon={<Sparkles />} tone="orange" foot={<><span>quality ≥ 70</span></>} />
      </div>
    ) : null}

    <div className="toolbar">
      <div className="toolbar-group">
        <div className="search-wrap"><Search /><input className="field" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by user, agent, or session ID" /></div>
        <Select
          size="sm"
          variant="toolbar"
          searchable={false}
          ariaLabel="Agent"
          value={agentFilter}
          onChange={(v) => setAgentFilter(v ?? "All agents")}
          options={[
            { value: "All agents", label: "All agents", icon: <Bot size={14} /> },
            ...sessionAgentIds.map((a) => {
              const meta = agentMeta(a);
              return {
                value: a,
                label: meta.name,
                description: meta.role,
                icon: <Bot size={14} />,
              };
            }),
          ]}
        />
        {showBeta ? <button className="btn small" type="button"><Filter />More filters</button> : null}
      </div>
      <div className="segmented" aria-label="Status filter">
        {FILTERS.map((x) => (
          <button key={x} className={status === x ? "active" : ""} onClick={() => setStatus(x)} type="button">{x}</button>
        ))}
      </div>
    </div>

    <section className="card data-card">
      <div className="card-head">
        <div><h2 className="card-title">Recent sessions</h2><span className="card-kicker">Real-time activity across production agents</span></div>
        <Pill tone="blue">Updated just now</Pill>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Topic</th>
            <th>Agent</th>
            <th>Started</th>
            <th>Duration</th>
            <th>Status</th>
            <th>Perception</th>
            <th>Quality</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const name = displayUserName(s);
            const avatarKey = sessionUserAvatarKey(s);
            const display = s.displayStatus || toDisplayStatus(s.status);
            const started = formatStarted(s.createdAt);
            const mood = moodLabel(s.perceptionMood);
            const score = s.qualityScore != null ? `${Math.round(Number(s.qualityScore))}%` : "—";
            const scoreNum = s.qualityScore != null ? Number(s.qualityScore) : null;
            const agent = agentMeta(s.agentId);
            const topic = displayTopic(s);
            const openSession = () => navigate(`/sessions/${s.sessionId}`);
            return (
              <tr
                key={s.sessionId}
                className="session-row-open"
                tabIndex={0}
                role="link"
                aria-label={`Open session ${name}`}
                onClick={openSession}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openSession();
                  }
                }}
              >
                <td>
                  <div className="person-cell">
                    <span className="avatar" style={{ background: avatarGradientForUserKey(avatarKey) }}>
                      {initialsFrom(name)}
                    </span>
                    <span>
                      <Link
                        className="cell-main"
                        href={`/sessions/${s.sessionId}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {name}
                      </Link>
                      <span className="cell-sub">{s.userEmail || s.sessionId}</span>
                    </span>
                  </div>
                </td>
                <td>
                  <span className="cell-main" title={topic}>{topic}</span>
                </td>
                <td>
                  <span className="cell-main" style={{ color: agent.color }}>{agent.name}</span>
                  <span className="cell-sub">{agent.role}</span>
                </td>
                <td>
                  <span className="cell-main">{started.time}</span>
                  <span className="cell-sub">{started.day}</span>
                </td>
                <td><span className="cell-main">{formatDuration(s.durationSec, s.status)}</span></td>
                <td>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <Pill tone={statusTone(display)}><span className="dot" />{display}</Pill>
                    {(s.providerDegraded || (Number(s.groq429Count) || 0) > 0) ? (
                      <span title={s.degradedReason || undefined}>
                        <Pill tone="orange">
                          {s.degradedReasonCode === "llm_failover"
                            ? "Provider failover"
                            : `Rate limited${Number(s.groq429Count) > 0 ? ` · ${s.groq429Count}` : ""}`}
                        </Pill>
                      </span>
                    ) : null}
                  </div>
                </td>
                <td>
                  {s.perceptionFusion ? (
                    <div className="perception-cell" title={fusionTooltip(s.perceptionFusion)}>
                      <Pill tone={s.perceptionFusion.tone}>{s.perceptionFusion.label}</Pill>
                      {Array.isArray(s.voiceSpark) && s.voiceSpark.length >= 2 ? (
                        <span className="perception-voice-spark" title="SER valence">
                          <VoiceSparkline values={s.voiceSpark} />
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <div className="perception-cell">
                      <Pill tone={moodTone(mood)}>{mood}</Pill>
                      {Array.isArray(s.voiceSpark) && s.voiceSpark.length >= 2 ? (
                        <span className="perception-voice-spark" title="SER valence">
                          <VoiceSparkline values={s.voiceSpark} />
                        </span>
                      ) : null}
                    </div>
                  )}
                </td>
                <td>
                  <span className="cell-main">{score}</span>
                  {scoreNum != null && (
                    <div className="progress-track" style={{ width: 54, marginTop: 5 }}>
                      <div className="progress-fill" style={{ width: `${Math.min(100, scoreNum)}%`, background: scoreNum < 60 ? "#e14d5a" : undefined }} />
                    </div>
                  )}
                </td>
                <td>
                  <div
                    className="row-action-wrap"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="row-action"
                      aria-label={`Actions for ${s.sessionId}`}
                      aria-expanded={menuId === s.sessionId}
                      disabled={busyId === s.sessionId}
                      onClick={() => setMenuId((cur) => (cur === s.sessionId ? null : s.sessionId))}
                    >
                      <MoreHorizontal />
                    </button>
                    {menuId === s.sessionId && (
                      <div className="row-menu" role="menu">
                        <button type="button" role="menuitem" onClick={() => { setMenuId(null); navigate(`/sessions/${s.sessionId}`); }}>
                          Open
                        </button>
                        <button type="button" role="menuitem" onClick={() => void flagSession(s.sessionId)}>
                          <Flag size={13} /> Flag for review
                        </button>
                        <button type="button" role="menuitem" onClick={() => void copySessionId(s.sessionId)}>
                          <Copy size={13} /> Copy session ID
                        </button>
                        <button type="button" role="menuitem" onClick={() => void downloadTranscript(s.sessionId)}>
                          <FileText size={13} /> Download transcript
                        </button>
                        {canExportReport ? (
                          <button type="button" role="menuitem" onClick={() => void exportReport(s.sessionId)}>
                            <Download size={13} /> Export report
                          </button>
                        ) : null}
                        <div className="row-menu-sep" />
                        <button
                          type="button"
                          role="menuitem"
                          className="danger"
                          onClick={() => { setMenuId(null); setDeleteId(s.sessionId); }}
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {loading && <div className="empty-state">Loading sessions…</div>}
      {!loading && rows.length === 0 && (
        <div className="empty-state">
          {sessions.length === 0 ? "No sessions yet." : "No sessions match these filters."}
        </div>
      )}
      {!loading && hasMore ? (
        <div className="table-load-more">
          <button
            type="button"
            className="btn"
            disabled={loadingMore}
            onClick={() => void loadMore()}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
      <div className="table-footer">
        <span>
          {loading
            ? "Loading…"
            : `Showing ${rows.length} of ${sessions.length} loaded${totalSessions > sessions.length ? ` · ${totalSessions} total` : ""}`}
        </span>
      </div>
    </section>

    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white border-[#dce5ef] sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Start a new AI session</DialogTitle>
          <DialogDescription>
            Pick an agent, copy a share link, or open the conversation app. Recipients must log in at pulse.igentify.in.
          </DialogDescription>
        </DialogHeader>
        <Select
          className="session-modal-field"
          label="Agent"
          ariaLabel="Choose agent"
          value={pickedAgent}
          onChange={(v) => setPickedAgent(v ?? "")}
          options={agentsCatalog.map((a) => ({
            value: a.agentId,
            label: a.name || a.agentId,
            icon: <Bot size={14} />,
          }))}
        />
        <label className="session-modal-field">
          Share link
          <div className="session-link-row">
            <input readOnly value={shareLink} aria-label="Share link" />
            <button type="button" className="btn" onClick={() => void copyLink()}><Copy size={14} />Copy link</button>
          </div>
        </label>
        <p className="session-link-hint">
          After login, the conversation app joins with this agent. Default workspace agent still applies if the link has no agent id.
        </p>
        <DialogFooter>
          <button className="btn" onClick={() => setOpen(false)}>Close</button>
          <a
            className="btn primary"
            href={shareLink}
            target="_blank"
            rel="noreferrer"
            onClick={() => { setOpen(false); showToast("Opening conversation app"); }}
          >
            <Video />Open with {pickedAgent}
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {toast && <div className="toast"><span className="toast-icon">✓</span><span><b>{toast}</b></span></div>}

    <Dialog open={Boolean(deleteId)} onOpenChange={(v) => { if (!v) setDeleteId(null); }}>
      <DialogContent className="bg-white border-[#dce5ef] sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Delete session?</DialogTitle>
          <DialogDescription>
            {(() => {
              const s = sessions.find((x) => x.sessionId === deleteId);
              const name = s ? displayUserName(s) : deleteId;
              const topic = s ? displayTopic(s) : "—";
              return (
                <>
                  This permanently removes session metadata and recording artifacts for{" "}
                  <b>{name}</b>
                  {topic && topic !== "—" ? <> · <b>{topic}</b></> : null}
                  . This cannot be undone.
                </>
              );
            })()}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button className="btn" onClick={() => setDeleteId(null)} disabled={busyId === deleteId}>Cancel</button>
          <button className="btn danger" onClick={() => void deleteSessionConfirmed()} disabled={busyId === deleteId}>
            <Trash2 size={14} />{busyId === deleteId ? "Deleting…" : "Delete session"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
